import React, { useEffect, useContext, useMemo, useRef } from 'react';
import clsx from 'clsx';
import { v4 as uuidv4 } from 'uuid';
import { useElementSize } from 'lib/hook';
import { TableContext } from './context';
import Cell from './cell';
import EditingCell from './editingCell';

const Row = (props) => {
    const {
        columns,
        row,
        rowIndex,
        height,
        className,
        style,
        cellClassName,
        cellStyle,
        showScrollY
    } = props;
    const {
        ScrollbarWidth,
        columnBordered,
        cellEllipsis,
        rowKey,
        currentRow,
        hover, hoverRow, setHoverRow,
        selectable, selectedRow, setSelectedRow,
        onSelectRowRef,
        setCellEllipsisRowHeight,
        timeout,
        editingCell, setEditingCell,
        editedValue, batchEditValues, setBatchEditValues,
        draggingRow
    } = useContext(TableContext);

    const bgHover = hover && (hoverRow || hoverRow === 0) && row[rowKey] === hoverRow;
    const bgSelected = useMemo(() => {
        let result = selectable;
        if (result) {
            if (currentRow || currentRow === 0) {
                result = row[rowKey] === currentRow;
            } else {
                result = (selectedRow || selectedRow === 0) && row[rowKey] === selectedRow;
            }
        } else if (draggingRow) {
            result = row[rowKey] === draggingRow;
        }
        return result;
    }, [row, rowKey, selectable, currentRow, selectedRow, draggingRow]);

    const ref = useRef();
    const { height: rowHeight } = useElementSize(ref);
    useEffect(() => {
        if (cellEllipsis && selectedRow === row[rowKey] && rowHeight > height) {
            clearTimeout(timeout.current);
            setCellEllipsisRowHeight((prev) => (
                prev[row[rowKey]] ? prev : { [row[rowKey]]: rowHeight + 8 }
            ));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowHeight]);

    const stopPropagation = useRef(false);

    return !!columns.length && (
        <div
            ref={ref}
            className={clsx(
                'h-fit flex',
                { 'bg-table-row dark:bg-black-light02': !bgHover && !bgSelected },
                { 'bg-table-row-hover dark:text-black': bgHover },
                { 'bg-table-row-selected dark:text-black': bgSelected },
                className
            )}
            style={{ minHeight: height, ...style }}
            onMouseLeave={() => setHoverRow('')}
            onMouseEnter={() => setHoverRow(row[rowKey])}
            onClick={(e) => {
                if (stopPropagation.current) {
                    e.stopPropagation();
                    stopPropagation.current = false;
                }
                setSelectedRow(row[rowKey]);
                onSelectRowRef.current?.(row[rowKey]);
            }}
        >
            {columns.map((col, colIndex) => {
                const width = colIndex === columns.length - 1 && showScrollY ?
                    col.width - ScrollbarWidth : col.width;
                const isEditing = editingCell.col === col.dataIndex &&
                    editingCell.row === row[rowKey];
                const outline = isEditing || (
                    batchEditValues.col && batchEditValues.col === col.dataIndex && (
                        (rowIndex - batchEditValues.rowStart) *
                        (rowIndex - batchEditValues.rowEnd) <= 0
                    )
                );
                return (
                    <div
                        key={col.dataIndex || uuidv4()}
                        className={clsx(
                            'table-row-cell flex items-center relative overflow-hidden',
                            {
                                'border-r':
                                    columnBordered &&
                                    colIndex !== columns.length - 1
                            },
                            {
                                'justify-center':
                                    col.align !== 'left' && col.align !== 'right'
                            },
                            { 'justify-start': col.align === 'left' },
                            { 'justify-end': col.align === 'right' },
                            { 'outline outline-2 outline-offset-[-2px]': outline },
                            { 'outline-table-tab-selected': outline },
                            { 'select-none': batchEditValues.col },
                            cellClassName?.(row, rowIndex, col, colIndex)
                        )}
                        style={{
                            width,
                            minHeight: height - 1,
                            height: isEditing ? height - 1 : undefined,
                            ...cellStyle?.(row, rowIndex, col, colIndex)
                        }}
                        onClick={() => {
                            if (!isEditing) {
                                setEditingCell({ col: col.dataIndex, row: row[rowKey] });
                            } else {
                                stopPropagation.current = true;
                            }
                        }}
                        onMouseEnter={() => {
                            if (batchEditValues.col) {
                                setBatchEditValues((prev) => ({ ...prev, rowEnd: rowIndex }));
                            }
                        }}
                    >
                        {isEditing ? (
                            <>
                                <EditingCell col={col} row={row} />
                                <div
                                    className="absolute bottom-0 right-0 w-1.5 h-1.5
                                        cursor-crosshair bg-table-tab-selected"
                                    onMouseDown={() => {
                                        if (editedValue.current.formatValue === undefined) {
                                            editedValue.current.formatValue = row[col.dataIndex];
                                        }
                                        setBatchEditValues((prev) => ({
                                            ...prev,
                                            col: col.dataIndex,
                                            rowStart: rowIndex,
                                            rowEnd: rowIndex,
                                            ...editedValue.current
                                        }));
                                    }}
                                />
                            </>
                        ) : (
                            <Cell
                                width={width}
                                col={col}
                                row={row}
                                rowIndex={rowIndex}
                                bgHover={bgHover}
                                bgSelected={bgSelected}
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default Row;
